import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import HeartLoader from '@/shared/ui/HeartLoader';
import DicomList from '@/features/study-detail/DicomList/DicomList.tsx';
import { MultiViewerContainer } from '@/features/study-detail/Viewer/MultiViewerContainer';
import { ViewerProvider } from '@/features/study-detail/Viewer/ViewerProvider';
import type { ViewerUrlStateChangeHandler } from '@/features/study-detail/Viewer/hooks/useViewerUrlSync';
import { parseViewerUrlState } from '@/features/study-detail/Viewer/utils/viewerUrlState';
import { ViewerErrorBoundary } from '@/features/study-detail/Viewer/ViewerErrorBoundary';
import { LayoutSubSidebar, LayoutBreadcrumbs } from '@/features/layout';
import { WorklistRail } from '@/features/worklist';
import {
  ReaderEmptyState,
  ReaderErrorState,
} from '@/features/reader-empty-state';
import { CopilotStudySync } from '@/features/copilot';
import {
  Report,
  StudyReadinessProvider,
  useStudyGatesReady,
  useStudyReadiness,
  clearStudyLoadGesture,
  usePendingStudyLoad,
} from '@/features/study-detail';

import {
  useGetStudyQuery,
  useGetStudiesListQuery,
} from '@/state/slices/studies';
import { useGetViewsQuery, useTrackViewMutation } from '@/state/slices/views';
import { resetSession } from '@/state/slices/measurementSession';
import DicomButtons from '@/features/study-detail/DicomButtons/DicomButtons';

import { useSettings } from '@/features/settings';
import { usePageLoading } from '@/hooks/usePageLoading';
import { useViewerPopup } from '@/features/study-detail/hooks/useViewerPopup';
import ReactSplit from 'react-split';
import { useResizableBarHeight } from '@/features/study-detail/DicomList/useResizableBarHeight';
import { useResizableLayout } from './hooks/useResizableLayout';
import { deriveReaderState } from './readerState';
import styles from './StudyDetail.module.css';

// The 4-state model (empty / loading / active / error) and what "100% ready"
// means live in readerState.ts + studyReadiness.tsx.

// If a gate never reports (a bug, an exotic study), reveal anyway rather than
// holding the page hostage behind the overlay.
const READINESS_TIMEOUT_MS = 15000;

/** Content-area blocking overlay for the loading state — one opaque loader
 *  for cold loads and study switches alike, so nothing pops in beneath it.
 *  Covers the page content only (header, sidebar and worklist rail stay
 *  interactive) and is kept mounted briefly after loading ends so it fades
 *  out over the fully-assembled study. */
function StudyLoadingOverlay({ visible }: { visible: boolean }) {
  const [rendered, setRendered] = useState(visible);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    if (visible) {
      setRendered(true);
      setLeaving(false);
      return undefined;
    }
    if (!rendered) return undefined;
    setLeaving(true);
    const timer = setTimeout(() => setRendered(false), 200);
    return () => clearTimeout(timer);
  }, [visible, rendered]);

  if (!rendered) return null;
  return (
    <div
      className={styles.stateOverlay}
      data-leaving={leaving || undefined}
      aria-busy="true"
    >
      <HeartLoader showText />
    </div>
  );
}

function StudyDetailContent() {
  const { settings, updateSetting } = useSettings();

  const { studyUuid, dicomIndex: dicomIndexParam } = useParams<{
    studyUuid: string;
    dicomIndex?: string;
  }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const selectedUuid =
    studyUuid && studyUuid !== 'null' ? studyUuid : undefined;

  const viewerUrlState = useMemo(
    () => parseViewerUrlState(dicomIndexParam, searchParams),
    [dicomIndexParam, searchParams]
  );

  const handleViewerUrlStateChange = useCallback<ViewerUrlStateChangeHandler>(
    ({ dicomNumber, search }, { replace }) => {
      if (!selectedUuid) return;
      const url = `/studies/${selectedUuid}/${dicomNumber}?${search}`;
      if (`${window.location.pathname}${window.location.search}` !== url) {
        navigate(url, { replace });
      }
    },
    [navigate, selectedUuid]
  );

  const { isDetached, handleToggleDetach, postGoToAnnotation } =
    useViewerPopup(selectedUuid);
  const { splitProps } = useResizableLayout();
  const dicomBar = useResizableBarHeight({
    storageKey: 'dicomBarHeight',
    min: 124,
    defaultHeight: 124,
  });

  // Ref bridge: ViewerProvider sets this, Report calls it.
  const goToAnnotationRef = useRef<
    ((dicomUuid: string, frameNumber: number) => void) | null
  >(null);

  const { data: study, error } = useGetStudyQuery(
    { uuid: selectedUuid! },
    { skip: !selectedUuid }
  );

  // Background work 'worklist-rail-data' (AP-778 — studyBackgroundWork.ts)
  const { data: studiesListData, isLoading: studiesListIsLoading } =
    useGetStudiesListQuery({ paginate: false });

  const [trackView] = useTrackViewMutation();

  // Measurement-session state is study-scoped (AP-764 I7 turn-off rules): a
  // selection, armed row, annotation map or undo stack from the previous study
  // must not survive navigation. Reset whenever the study changes.
  const sessionDispatch = useDispatch();
  useEffect(() => {
    sessionDispatch(resetSession());
  }, [sessionDispatch, selectedUuid]);

  // Background work 'view-tracking' (AP-778 — see studyBackgroundWork.ts)
  useEffect(() => {
    if (selectedUuid) {
      trackView({ objectType: 'STUDY', objectUuid: selectedUuid });
    }
  }, [selectedUuid, trackView]);

  // Background work 'landing-recents' (AP-778 — studyBackgroundWork.ts).
  // Recents feed the landing page AND the error state (which renders the
  // same calm canvas with recents to jump back into).
  const { data: viewsData, isLoading: viewsIsLoading } = useGetViewsQuery(
    { page_size: 3, object_type: 'STUDY' },
    { skip: !!selectedUuid && !error, refetchOnMountOrArgChange: true }
  );

  const studiesList = studiesListData?.results || [];
  const recentlyViewed = viewsData?.results || [];
  const hasPendingStudies = studiesList.some(
    (s) => s.assignedToMe && s.reportStatus !== 'FINAL'
  );

  // --- Reader state derivation (empty / loading / active / error).
  // `study` is RTK-keyed by the selected uuid, so its presence means
  // the SELECTED study's data has arrived; the gates then say whether the
  // viewer + worksheet have finished bringing it fully up.
  const gatesReady = useStudyGatesReady(selectedUuid, {
    requireViewer: !isDetached,
  });
  const isStudyReady = !!selectedUuid && !!study && gatesReady;

  // Leaving a study forgets its gates, so returning to it later re-runs the
  // full readiness handshake instead of trusting stale `ready` flags.
  const { resetGates } = useStudyReadiness();
  const prevSelectedRef = useRef(selectedUuid);
  useEffect(() => {
    const prev = prevSelectedRef.current;
    prevSelectedRef.current = selectedUuid;
    if (prev && prev !== selectedUuid) resetGates(prev);
  }, [selectedUuid, resetGates]);

  // Reveal latch: which study the page has committed to showing. Once a study
  // is revealed it STAYS revealed through later in-study loading (dicom
  // navigation flips the viewer gate) — only selecting a different study
  // re-enters a loading state.
  const [revealedUuid, setRevealedUuid] = useState<string | null>(null);
  useEffect(() => {
    if (isStudyReady && selectedUuid) setRevealedUuid(selectedUuid);
  }, [isStudyReady, selectedUuid]);

  // Safety valve: never trap the reader behind the overlay.
  useEffect(() => {
    if (!selectedUuid || isStudyReady) return undefined;
    const timer = setTimeout(() => {
      // eslint-disable-next-line no-console -- readiness gate failed to report; surface it
      console.warn('[reader] readiness timed out — revealing study anyway');
      setRevealedUuid(selectedUuid);
    }, READINESS_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [selectedUuid, isStudyReady]);

  const readerState = deriveReaderState({
    selectedUuid,
    hasStudyData: !!study,
    hasFetchError: !!error,
    revealedUuid,
    gatesReady,
  });

  // Click-time loading gesture (see studyLoadGesture.ts): the worklist click
  // showed the overlay urgently, before the router transition landed. Keep it
  // up until the clicked study is revealed (or fails), and drop a stale
  // gesture if the user ends up somewhere else entirely.
  const pendingGesture = usePendingStudyLoad();
  useEffect(() => {
    if (!pendingGesture) return;
    const arrived =
      pendingGesture.uuid === selectedUuid &&
      (readerState === 'active' || readerState === 'error');
    const abandoned = readerState === 'empty';
    if (arrived || abandoned) clearStudyLoadGesture();
  }, [pendingGesture, selectedUuid, readerState]);

  // The overlay is opaque, so there is no stale-study rendering: during
  // loading only the loader is visible, and the new study mounts and warms
  // up invisibly beneath it once its data arrives.
  const displayStudy = study;

  // Breadcrumb name, most-immediate source first: the click gesture already
  // carries the target's patient name (same-frame update, R6), the fetched
  // study covers every other path. Bare "Reader" only when neither knows.
  const patientName = pendingGesture?.patientName
    ? pendingGesture.patientName
    : displayStudy &&
      `${displayStudy.patientFirstName} ${displayStudy.patientLastName}`;
  const breadcrumbs = useMemo(
    () =>
      patientName
        ? [{ label: 'Reader', to: '/studies' }, { label: patientName }]
        : [{ label: 'Reader' }],
    [patientName]
  );

  // Landing data only — study loading is owned by the AP-778 overlay.
  usePageLoading(!selectedUuid && (studiesListIsLoading || viewsIsLoading));

  // Determine main content based on state
  let content: React.ReactNode;

  if (readerState === 'empty') {
    content = (
      <ReaderEmptyState
        hasPendingStudies={hasPendingStudies}
        recentlyViewed={recentlyViewed}
        hasAssignments={studiesList.some((s) => s.assignedToMe)}
      />
    );
  } else if (readerState === 'error') {
    content = <ReaderErrorState recentlyViewed={recentlyViewed} />;
  } else if (!displayStudy) {
    // Study data still in flight: the opaque overlay owns the visuals.
    content = null;
  } else {
    // While the next study loads, the previous study's viewer stays mounted.
    // Keep it off the URL so its layout and panes don't leak into the new one.
    const isShownStudySelected = displayStudy.uuid === selectedUuid;
    content = isDetached ? (
      <div className={styles.container} data-expanded>
        <div className={styles.reportPanel} data-expanded>
          <Report
            studyUuid={displayStudy.uuid}
            study={displayStudy}
            onGoToAnnotation={postGoToAnnotation}
          />
        </div>
      </div>
    ) : (
      <div className={styles.container}>
        <ReactSplit {...splitProps} className={styles.splitContainer}>
          <div className={styles.leftSection}>
            <ViewerProvider
              key={displayStudy.uuid}
              studyUuid={displayStudy.uuid}
              dicoms={displayStudy.dicoms}
              goToAnnotationRef={goToAnnotationRef}
              urlState={isShownStudySelected ? viewerUrlState : undefined}
              onUrlStateChange={
                isShownStudySelected ? handleViewerUrlStateChange : undefined
              }
            >
              <div className={styles.viewerPanel} data-viewer-area="true">
                <ViewerErrorBoundary>
                  <MultiViewerContainer
                    isDetached={isDetached}
                    onToggleDetach={handleToggleDetach}
                  />
                </ViewerErrorBoundary>
              </div>
              <div
                className={styles.dicomResizeHandle}
                title="Drag to resize the thumbnail bar"
                {...dicomBar.handleProps}
              />
              <div
                className={styles.dicomListPanel}
                data-dicom-list-area="true"
                style={{ height: dicomBar.height }}
              >
                <DicomList dicoms={displayStudy.dicoms} />
                <DicomButtons />
              </div>
            </ViewerProvider>
          </div>
          <div className={styles.reportPanel}>
            <Report
              key={displayStudy.uuid}
              studyUuid={displayStudy.uuid}
              study={displayStudy}
              onGoToAnnotation={(d, f) => goToAnnotationRef.current?.(d, f)}
            />
          </div>
        </ReactSplit>
      </div>
    );
  }

  const overlayVisible =
    readerState === 'loading' ||
    // Gesture window: clicked in the rail, router transition not landed yet
    // (selectedUuid is still the old study, readerState still 'active').
    (pendingGesture !== null && pendingGesture.uuid !== selectedUuid);

  return (
    <>
      {/* Background work 'copilot-study-sync' (AP-778 — studyBackgroundWork.ts) */}
      <CopilotStudySync />
      <LayoutBreadcrumbs items={breadcrumbs} />
      <LayoutSubSidebar
        title="Worklist"
        defaultCollapsed={settings.collapsedAssignments}
        onCollapsedChange={(collapsed) =>
          updateSetting({ collapsedAssignments: collapsed })
        }
      >
        <WorklistRail studies={studiesList} activeStudyUuid={selectedUuid} />
      </LayoutSubSidebar>
      <div className={styles.contentArea}>
        {content}
        <StudyLoadingOverlay visible={overlayVisible} />
      </div>
    </>
  );
}

export default function StudyDetail() {
  return (
    <StudyReadinessProvider>
      <StudyDetailContent />
    </StudyReadinessProvider>
  );
}
